/**
 * personDayLoad.ts
 * Pure model for the Employee Schedule L1 redesign (2026-07-25, design rounds
 * 1–11): every visible day gets a LOAD — the sum of the day-share % of every
 * task bar covering it — plus flags for absence, weekend and stat holidays.
 * All figures DERIVE from the assignment bars the API already ships
 * (EmployeeBar.percent + start/end dates); nothing here is authored separately,
 * so the summary can never disagree with the plan below it (the round-10
 * "stacked but where are the tasks?" bug class).
 *
 * Semantics (locked in the design spec; COUNT-ONCE rework 2026-07-30):
 *  - day load    = Σ over SOURCES (jobs) covering the day, each source counted
 *                  ONCE. A task bar's percent is the person's intentional SHARE
 *                  of that job for the week (EmployeeSourceAllocation — a 10%
 *                  reviewer reads 10 on every task of the job; 100 = presence
 *                  fallback, BUG-2026-0196). Summing per BAR made one job's two
 *                  same-day tasks read 200%; per the count-once staffing model
 *                  a person's intensity is per source·week, so a day is "one
 *                  job = its share", and >100 means genuinely over-allocated.
 *  - over        = load > 100 (Σ shares of the day's jobs exceeds a person)
 *  - away        = an absence bar (Vacation/Sick/FieldWork/Other) covers the day
 *  - weekend     = Sat/Sun (BST weeks start Saturday — cols 0 and 1). Task bars
 *                  DO NOT paint weekend days (2026-07-30): a continuous test's
 *                  booking runs the machine through Sat/Sun, not the person —
 *                  same rule the scenario views' synthesized bars follow
 *                  (2026-07-26). Absences still mark weekend days as away.
 *  - statWorked  = a Task bar covers a stat-holiday date
 */

import type { EmployeeBar } from '../services/scheduleEmployeeBars';
import { addDaysISO, isValidISODate } from '../components/schedule/employeeScheduleShared';

export interface DayLoadPart {
  taskName: string;
  percent: number;
  bar: EmployeeBar;
}

export interface DayLoad {
  /** ISO date of the day. */
  date: string;
  /** 0 = Sat … 6 = Fri within its BST week. */
  dayIndex: number;
  /** Σ day-share % of Task bars covering this day. */
  load: number;
  /** The bars behind `load` — powers the arithmetic tooltip. */
  parts: DayLoadPart[];
  /** An absence bar covers this day. */
  away: boolean;
  /** Sat or Sun. */
  weekend: boolean;
  /** The day is a stat holiday. */
  stat: boolean;
}

export interface WeekLoadAggregate {
  weekStart: string;
  days: DayLoad[];
  /** Busy working days ÷ working days (holiday-aware), 0–100 — PRESENCE. */
  busyPercent: number;
  /**
   * Intensity-aware week utilization (BUG-2026-0196): the AVERAGE daily load
   * across working days (`weekUtilizationPercent`). Equals busyPercent when
   * every job is a 100% share; a 10% reviewer present all week reads 10, not
   * 100. This is the figure the Employee Schedule pill / %-line prints.
   */
  utilizationPercent: number;
  /** Every working day is covered by an absence. */
  away: boolean;
  /** Any Sat/Sun day carries load. */
  weekendWork: boolean;
  /** A Task bar covers a stat-holiday date this week. */
  statWorked: boolean;
  /** Any day's load exceeds 100. */
  over: boolean;
  /** Anything at all this week (load or absence). */
  any: boolean;
}

const ABSENCE_KINDS = new Set(['Vacation', 'Sick', 'FieldWork', 'Other']);

/**
 * The identity a day's load is counted ONCE per: the bar's job (quote or WO),
 * else its scenario (synthesized this-draft bars), else its name.
 */
function barSourceKey(b: EmployeeBar): string {
  return b.quoteKey ?? b.workOrderKey ?? b.scenarioKey ?? b.name;
}

/** Per-day loads for one BST week (7 entries, Sat..Fri) from a person's bars. */
export function buildWeekDayLoads(
  bars: readonly EmployeeBar[],
  weekStartSat: string,
  holidays?: ReadonlySet<string>,
): DayLoad[] {
  const days: DayLoad[] = [];
  if (!isValidISODate(weekStartSat)) return days;
  for (let d = 0; d < 7; d++) {
    const date = addDaysISO(weekStartSat, d);
    const day: DayLoad = {
      date, dayIndex: d, load: 0, parts: [], away: false,
      weekend: d < 2, stat: holidays?.has(date) ?? false,
    };
    // COUNT-ONCE (2026-07-30): each job contributes its strongest bar's %
    // once per day — a job's two same-day tasks are one 100% presence, not 200.
    const perSource = new Map<string, number>();
    for (const b of bars) {
      if (b.startDate > date || b.endDate < date) continue;
      if (ABSENCE_KINDS.has(b.kind)) {
        day.away = true;
        continue;
      }
      // People don't work weekends — a booking spanning Sat/Sun runs the
      // MACHINE, not the person (matches the scenario synth-bar rule).
      if (day.weekend) continue;
      const key = barSourceKey(b);
      perSource.set(key, Math.max(perSource.get(key) ?? 0, b.percent));
      day.parts.push({ taskName: b.name, percent: b.percent, bar: b });
    }
    for (const pct of perSource.values()) day.load += pct;
    days.push(day);
  }
  return days;
}

/** Aggregate one week's day loads into the collapsed-pill / %-line figures. */
export function aggregateWeek(
  weekStartSat: string,
  days: readonly DayLoad[],
): WeekLoadAggregate {
  const working = days.filter((d) => !d.weekend && !d.stat);
  const busyDays = working.filter((d) => d.load > 0).length;
  const denom = working.length || 1;
  return {
    weekStart: weekStartSat,
    days: [...days],
    busyPercent: Math.round((busyDays / denom) * 100),
    utilizationPercent: weekUtilizationPercent(days),
    away: working.length > 0 && working.every((d) => d.away || d.load === 0) && working.some((d) => d.away),
    weekendWork: days.some((d) => d.weekend && d.load > 0),
    statWorked: days.some((d) => d.stat && d.load > 0),
    over: days.some((d) => d.load > 100),
    any: days.some((d) => d.load > 0 || d.away),
  };
}

/** Convenience: aggregates for every visible week for one person's bars. */
export function buildPersonWeekLoads(
  bars: readonly EmployeeBar[],
  weekStarts: readonly string[],
  holidays?: ReadonlySet<string>,
): WeekLoadAggregate[] {
  return weekStarts.map((ws) => {
    const weekBars = bars.filter((b) => b.weekStart === ws);
    return aggregateWeek(ws, buildWeekDayLoads(weekBars, ws, holidays));
  });
}

/**
 * Intensity-aware week utilization: the AVERAGE daily load across working days
 * (weekends/stats excluded). Unlike busyPercent (presence — days touched ÷
 * working days), this reflects HOW LOADED the days are: a 25% mentor across
 * 4 of 5 days reads 20%, not 80% (Scenario Phase-3 feedback 2026-07-26).
 */
export function weekUtilizationPercent(days: readonly DayLoad[]): number {
  const working = days.filter((d) => !d.weekend && !d.stat);
  if (!working.length) return 0;
  return Math.round(working.reduce((sum, d) => sum + d.load, 0) / working.length);
}

const DOW = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

/**
 * The arithmetic tooltip: "Thu — 100% · Adin's Tests 100% (2 tasks)".
 * Count-once (2026-07-30): grouped per job so the listed percents actually
 * sum to `load` — two same-day tasks of one job read as one 100% entry.
 */
export function dayLoadTitle(day: DayLoad): string {
  const name = DOW[day.dayIndex] ?? '';
  if (day.away && day.load === 0) return `${name} — day off`;
  if (day.load === 0) return `${name} — free${day.stat ? ' (stat holiday)' : ''}`;
  const groups = new Map<string, { label: string; percent: number; taskNames: string[] }>();
  for (const p of day.parts) {
    const key = barSourceKey(p.bar);
    const label = p.bar.workOrderName ?? p.bar.projectName ?? p.bar.quoteNumber ?? p.taskName;
    const g = groups.get(key) ?? { label, percent: 0, taskNames: [] };
    g.percent = Math.max(g.percent, p.percent);
    g.taskNames.push(p.taskName);
    groups.set(key, g);
  }
  const parts = [...groups.values()]
    .map((g) => `${g.label} ${g.percent}%${g.taskNames.length > 1 ? ` (${g.taskNames.length} tasks)` : ''}`)
    .join(' + ');
  const notes = [
    day.stat ? 'stat holiday!' : '',
    day.weekend ? 'weekend' : '',
    day.away ? 'also has a day off booked' : '',
  ].filter(Boolean).join(', ');
  return `${name} — ${day.load}%${notes ? ` (${notes})` : ''} · ${parts}`;
}
